import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import RoleFormModal from '../components/roles/RoleFormModal';
import { useApi } from '../api/client';
import { createRole, deleteRole, listPermissions, listRoles, updateRole } from '../api/roles';
import useDebounce from '../hooks/useDebounce';

const ROLE_TONE = { admin: 'admin', tresorier: 'tresorier', coordinateur: 'coordinateur', secretaire: 'pelerinage' };

export default function RolesPage() {
  const t = useT();
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    if (!useApi) return;
    setLoading(true);
    try {
      const [r, p] = await Promise.all([listRoles(), listPermissions()]);
      setRoles(r || []);
      setPermissions(p || []);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) =>
      r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
    );
  }, [roles, debouncedQuery]);

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        const updated = await updateRole(editing.id, data);
        setRoles((prev) => prev.map((r) => (r.id === editing.id ? updated : r)));
        toast.success(t('roles_page.toast_updated', { name: updated.name }));
      } else {
        const created = await createRole(data);
        setRoles((prev) => [created, ...prev]);
        toast.success(t('roles_page.toast_created', { name: created.name }));
      }
      setFormOpen(false);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteRole(deleting.id);
      setRoles((prev) => prev.filter((r) => r.id !== deleting.id));
      toast.success(t('roles_page.toast_deleted'));
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setDeleting(null);
    }
  };

  if (!useApi) {
    return (
      <Card title={t('pages.roles.title')} subtitle={t('pages.roles.subtitle')}>
        <EmptyState icon="🔌" title={t('roles_page.api_required')} />
      </Card>
    );
  }

  const columns = [
    { key: 'name', header: t('roles_page.field_name'), render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Badge tone={ROLE_TONE[r.name] || 'admin'}>{r.name}</Badge>
          {r.system && <span className="form-hint" style={{ fontStyle: 'italic', margin: 0 }}>· {t('roles_page.system_role')}</span>}
        </span>
      ) },
    { key: 'description', header: t('roles_page.field_description'), render: (r) => r.description || '—' },
    { key: 'permissions', header: t('roles_page.field_permissions'),
      render: (r) => t('roles_page.permissions_count', { count: (r.permissions || []).length }) },
    { key: 'users', header: t('users.title'), render: (r) => t('roles_page.user_count', { count: r.userCount }) },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => { setEditing(r); setFormOpen(true); }}>{t('common.edit')}</button>
          {!r.system && (
            <button type="button" className="action-btn danger" onClick={() => setDeleting(r)}>{t('common.delete')}</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('common.search')} />
        <Button variant="gold" onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('roles_page.new_role')}</Button>
      </SearchRow>

      <Card title={t('pages.roles.title')} subtitle={t('pages.roles.subtitle')} padded={false}>
        <DataTable
          columns={columns}
          rows={filtered}
          empty={<EmptyState title={loading ? t('common.loading') : t('common.no_data')} />}
        />
      </Card>

      <RoleFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
        allPermissions={permissions}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('roles_page.delete_confirm_title')}
        body={deleting ? t('roles_page.delete_confirm_body', { name: deleting.name }) : ''}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
